import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { FC, ChangeEvent } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ArrowBack } from "@mui/icons-material";
import { ImageUploadModal } from "../creationSidebarEasymaps/newModalAddIcons";
import api from "../../../services/api";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../alertModal";
import type {
  TableRow,
  TextRow,
  AreaRow,
  ModalAddMultipleProps,
} from "./types";
import { validateRow, validateTextRow, validateAreaRow } from "./validation";
import type { ValidNamesLookup } from "./validation";
import {
  parseCsvFile,
  rowsToCsv,
  exampleCsvTemplate,
  mapRecordToRow,
} from "./csv";
import type { CsvHeaderKey } from "./csv";
import {
  normalizeType,
  defaultIconNameForType,
  fillRowDefaults,
} from "./helpers";
import { elementToRow, textElementToRow, areaElementToRow } from "./mappers";
import ElementsTable from "./elementsTable";
import type { ElementsTableHandle } from "./elementsTable";
import ShapeElementsTable from "./shapeElementsTable";
import type { ShapeElementsTableHandle } from "./shapeElementsTable";

// Dispara o download de uma string CSV no navegador (Blob + âncora temporária).
function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

type TabMode = "add" | "edit";
type EditSubTab = "hosts" | "textos" | "areas";

export const ModalAddMultiple: FC<ModalAddMultipleProps> = ({
  open,
  onClose,
  produtos = [] as any[],
  iconsElement = [] as any[],
  mapId,
  setSnackbar,
  onSaveComplete,
  setIconsElement = () => {},
  elements = [] as any[],
}: ModalAddMultipleProps) => {
  const tableRef = useRef<ElementsTableHandle>(null);
  const textTableRef = useRef<ShapeElementsTableHandle>(null);
  const areaTableRef = useRef<ShapeElementsTableHandle>(null);

  // Input de arquivo oculto usado pela importação de CSV
  const importInputRef = useRef<HTMLInputElement>(null);

  // Aba ativa e datasets independentes por aba
  const [activeTab, setActiveTab] = useState<TabMode>("add");
  const [addData, setAddData] = useState<TableRow[]>([]);
  const [editData, setEditData] = useState<TableRow[]>([]);
  // Subaba da edição (Hosts / Textos / Áreas) e seus datasets independentes.
  const [editSubTab, setEditSubTab] = useState<EditSubTab>("hosts");
  const [editTextData, setEditTextData] = useState<TextRow[]>([]);
  const [editAreaData, setEditAreaData] = useState<AreaRow[]>([]);

  // Caches de dados da API (hosts, mapas, POPs)
  const [hostsCache, setHostsCache] = useState<any[]>([]);
  const [mapsCache, setMapsCache] = useState<any[]>([]);
  const [popsCache, setPopsCache] = useState<any[]>([]);
  const [tagsCache, setTagsCache] = useState<string[]>([]);
  // Estados de UI (salvando, confirmação)
  const [saving, setSaving] = useState(false);
  const [confirmMode, setConfirmMode] = useState<TabMode | null>(null);
  // Exclusão em massa (aba Editar): confirmação e estado de "excluindo".
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openModalImg, setOpenModalImg] = useState(false);
  // Âncora do menu de ações CSV (importar / exportar / modelo)
  const [csvMenuAnchor, setCsvMenuAnchor] = useState<HTMLElement | null>(null);

  const link = useMemo(() => produtos?.[0]?.AcessoLink?.[0]?.link, [produtos]);

  const validNamesLookup = useMemo<ValidNamesLookup>(
    () => ({
      hosts: new Set(hostsCache.map((h: any) => h.host)),
      pops: new Set(popsCache.map((p: any) => p.titulo)),
      maps: new Set(mapsCache.map((m: any) => m.titulo)),
      icons: new Set((iconsElement || []).map((i: any) => i.nome)),
    }),
    [hostsCache, popsCache, mapsCache, iconsElement],
  );

  // Controle de alterações não salvas
  const [hasChanges, setHasChanges] = useState(false);
  const [unsavedAlertOpen, setUnsavedAlertOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "closeModal" | "navigateBack" | null
  >(null);

  // Ícone padrão para novas linhas
  const defaultIcon = useMemo(
    () => iconsElement?.find((i: any) => i.nome === "_roteador"),
    [iconsElement],
  );

  // Criação de linha vazia com valores padrão
  const createEmptyRow = useCallback(
    (): TableRow => ({
      id: uuidv4(),
      type: "Host",
      selectedName: null,
      title: null,
      iconId: defaultIcon?.nome || null,
      lat: null,
      long: null,
      hostItems: [],
      description: null,
      tags: [],
      _errors: {},
      checked: false,
    }),
    [defaultIcon],
  );

  // Preenche padrões e valida cada linha (retorna nova referência → re-render).
  const validateRows = useCallback(
    (rows: TableRow[]): TableRow[] => {
      rows.forEach((row) => {
        fillRowDefaults(row, iconsElement);
        row._errors = validateRow(row, validNamesLookup);
      });
      return [...rows];
    },
    [iconsElement, validNamesLookup],
  );

  // Refs para acesso estável dentro do efeito de abertura (deps = [open])
  const fetchHostsRef = useRef<() => void>(() => {});
  const fetchMapsAndPopsRef = useRef<() => void>(() => {});
  const fetchTagsRef = useRef<() => void>(() => {});
  const createEmptyRowRef = useRef(createEmptyRow);
  const elementsRef = useRef(elements);
  const iconsElementRef = useRef(iconsElement);

  useEffect(() => {
    createEmptyRowRef.current = createEmptyRow;
  }, [createEmptyRow]);
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);
  useEffect(() => {
    iconsElementRef.current = iconsElement;
  }, [iconsElement]);

  // Funções de busca na API
  const fetchHosts = useCallback(async () => {
    if (!link) return;
    try {
      const response = await api.post("/easyMaps/zabbix/hosts", { link });
      if (response.data?.status === "Error") throw new Error();
      setHostsCache(response.data || []);
    } catch {
      setHostsCache([]);
      setSnackbar?.({
        children: "Não foi possível buscar os hosts.",
        severity: "error",
      });
    }
  }, [link, setSnackbar]);

  const fetchMapsAndPops = useCallback(async () => {
    try {
      const response = await api.get("/easyMaps/map/findMany");
      if (response.data?.status === "Error") throw new Error();
      const allMaps = response.data || [];
      setMapsCache(allMaps.filter((m: any) => m.tipo !== 2));
      setPopsCache(allMaps.filter((m: any) => m.tipo === 2));
    } catch {
      setMapsCache([]);
      setPopsCache([]);
      setSnackbar?.({
        children: "Não foi possível buscar os mapas e POPs.",
        severity: "error",
      });
    }
  }, [setSnackbar]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get("/easyMaps/tags/findMany");
      if (response.data?.status === "Error") throw new Error();
      setTagsCache(Array.isArray(response.data) ? response.data : []);
    } catch {
      setTagsCache([]);
      setSnackbar?.({
        children: "Não foi possível buscar as tags.",
        severity: "error",
      });
    }
  }, [setSnackbar]);

  // Registra uma tag nova no cache local para que fique pesquisável nas demais
  // linhas. A persistência real acontece no save dos elementos.
  const handleCreateTag = useCallback((tag: string) => {
    setTagsCache((prev) =>
      prev.some((t) => t.toLowerCase() === tag.toLowerCase())
        ? prev
        : [...prev, tag],
    );
  }, []);

  useEffect(() => {
    fetchHostsRef.current = fetchHosts;
  }, [fetchHosts]);
  useEffect(() => {
    fetchMapsAndPopsRef.current = fetchMapsAndPops;
  }, [fetchMapsAndPops]);
  useEffect(() => {
    fetchTagsRef.current = fetchTags;
  }, [fetchTags]);

  //  Efeitos de ciclo de vida do modal (dispara somente ao abrir/fechar)
  useEffect(() => {
    if (!open) {
      setAddData([]);
      setEditData([]);
      setEditTextData([]);
      setEditAreaData([]);
      setHostsCache([]);
      setMapsCache([]);
      setPopsCache([]);
      setTagsCache([]);
      setConfirmMode(null);
      setConfirmDelete(false);
      setDeleting(false);
      setHasChanges(false);
      setActiveTab("add");
      setEditSubTab("hosts");
      return;
    }
    fetchHostsRef.current();
    fetchMapsAndPopsRef.current();
    fetchTagsRef.current();
    setAddData([createEmptyRowRef.current()]);
    const els = elementsRef.current || [];
    setEditData(
      els
        .filter((el: any) => el.tipo <= 2)
        .map((el: any) => elementToRow(el, iconsElementRef.current)),
    );
    setEditTextData(
      els.filter((el: any) => el.tipo === 3).map((el: any) => textElementToRow(el)),
    );
    setEditAreaData(
      els.filter((el: any) => el.tipo === 4).map((el: any) => areaElementToRow(el)),
    );
    setHasChanges(false);
    setActiveTab("add");
    setEditSubTab("hosts");
  }, [open]);

  const handleCloseConfirmed = () => {
    setAddData([]);
    setEditData([]);
    setEditTextData([]);
    setEditAreaData([]);
    setHostsCache([]);
    setMapsCache([]);
    setPopsCache([]);
    setTagsCache([]);
    setConfirmMode(null);
    setHasChanges(false);
    setEditSubTab("hosts");
    onClose();
  };

  const handleClose = () => {
    if (hasChanges) {
      setPendingAction("closeModal");
      setUnsavedAlertOpen(true);
      return;
    }
    handleCloseConfirmed();
  };

  const handleUnsavedConfirm = () => {
    setUnsavedAlertOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    setHasChanges(false);
    if (action === "closeModal") {
      handleCloseConfirmed();
    } else if (action === "navigateBack") {
      onClose();
      window.history.back();
    }
  };

  const handleUnsavedCancel = () => {
    const action = pendingAction;
    setPendingAction(null);
    setUnsavedAlertOpen(false);
    if (action === "navigateBack") {
      window.history.pushState(
        { modalAddMultiple: true },
        "",
        window.location.href,
      );
    }
  };

  // Recebe novas linhas da tabela (já revalidadas) e direciona para a aba ativa.
  const handleTableDataChange = (rows: TableRow[]) => {
    if (activeTab === "add") setAddData(rows);
    else setEditData(rows);
    setHasChanges(true);
  };

  // Recebe novas linhas das tabelas de Texto/Área (edição) e direciona para o
  // dataset correspondente.
  const handleShapeDataChange = (
    sub: "textos" | "areas",
    rows: (TextRow | AreaRow)[],
  ) => {
    if (sub === "textos") setEditTextData(rows as TextRow[]);
    else setEditAreaData(rows as AreaRow[]);
    setHasChanges(true);
  };

  //  Ações exclusivas da aba "Adicionar"

  const handleAddRow = () => {
    const current = tableRef.current?.getRows() ?? addData;
    setAddData(validateRows([...current, createEmptyRow()]));
    setHasChanges(true);
  };

  const handleDeleteSelected = () => {
    const data = tableRef.current?.getRows() ?? addData;
    const remaining = data.filter((row) => !row.checked);
    setAddData(
      validateRows(remaining.length === 0 ? [createEmptyRow()] : remaining),
    );
    setHasChanges(true);
  };

  //  Salvamento (adição = create / edição = update)

  // Valida a aba ativa e, se não houver erros, abre a confirmação.
  const validateActiveAndConfirm = (mode: TabMode) => {
    if (mode === "add") {
      const data = tableRef.current?.getRows() ?? addData;
      let hasErr = false;
      data.forEach((row) => {
        fillRowDefaults(row, iconsElement);
        row._errors = validateRow(row, validNamesLookup);
        if (Object.keys(row._errors).length > 0) hasErr = true;
      });
      setAddData([...data]);
      if (hasErr) return;
      setConfirmMode(mode);
      return;
    }

    // Edição: valida os três datasets (Hosts/Textos/Áreas). Apenas a subaba
    // montada tem ref; as demais leem do estado (sincronizado a cada edição).
    const hosts = (tableRef.current?.getRows() as TableRow[]) ?? editData;
    const texts = (textTableRef.current?.getRows() as TextRow[]) ?? editTextData;
    const areas = (areaTableRef.current?.getRows() as AreaRow[]) ?? editAreaData;

    let hostErr = false;
    hosts.forEach((row) => {
      fillRowDefaults(row, iconsElement);
      row._errors = validateRow(row, validNamesLookup);
      if (Object.keys(row._errors).length > 0) hostErr = true;
    });
    let textErr = false;
    texts.forEach((row) => {
      row._errors = validateTextRow(row);
      if (Object.keys(row._errors).length > 0) textErr = true;
    });
    let areaErr = false;
    areas.forEach((row) => {
      row._errors = validateAreaRow(row);
      if (Object.keys(row._errors).length > 0) areaErr = true;
    });

    setEditData([...hosts]);
    setEditTextData([...texts]);
    setEditAreaData([...areas]);

    if (hostErr || textErr || areaErr) {
      // Leva o usuário até a subaba que contém o primeiro erro.
      if (hostErr) setEditSubTab("hosts");
      else if (textErr) setEditSubTab("textos");
      else setEditSubTab("areas");
      return;
    }
    setConfirmMode(mode);
  };

  // Resolve o mapRefId (POP/Mapa) a partir do nome selecionado, no momento do
  // save — espelha o que o create individual faz. Cai no row.mapRefId (já
  // resolvido na seleção) e nunca usa `id`/`elementId` como mapRef.
  const resolveMapRefId = (row: TableRow): string | null => {
    if (row.type === "POP")
      return (
        popsCache.find((p: any) => p.titulo === row.selectedName)?.id ??
        row.mapRefId ??
        null
      );
    if (row.type === "Mapa")
      return (
        mapsCache.find((m: any) => m.titulo === row.selectedName)?.id ??
        row.mapRefId ??
        null
      );
    return null; // Host não referencia mapa
  };

  // O grid guarda o NOME do ícone; o backend espera o UUID (img). Converte na
  // hora de salvar (mesmo contrato do PUT/POST individual de /easyMaps/element).
  const resolveImgId = (row: TableRow): string | null =>
    iconsElement.find((i: any) => i.nome === row.iconId)?.id ?? null;

  const confirmSave = async () => {
    setConfirmMode(null);
    setSaving(true);
    const data = tableRef.current?.getRows() ?? addData;

    // Monta payload para a API: transforma cada linha em um objeto de elemento
    const elementsData = data.map((row) => {
      let hostData = null;
      if (row.type === "Host") {
        const host = hostsCache.find((h: any) => h.host === row.selectedName);
        hostData = host
          ? { hostid: host.hostid, host: host.host, itens: row.hostItems }
          : null;
      }
      return {
        titulo: row.title || row.selectedName || "",
        tipo: row.type === "Host" ? 0 : row.type === "Mapa" ? 1 : 2,
        img: resolveImgId(row),
        mapRefId: resolveMapRefId(row),
        mapId,
        tags: row.tags ?? [],
        configuracao: {
          coordenadas: { lat: row.lat, long: row.long },
          hosts: row.type === "Host" && hostData ? [hostData] : [],
          produtoId: produtos?.[0]?.id,
          descricao: row.description || null,
        },
      };
    });

    try {
      await api.post("/easyMaps/element/createManyPing", {
        elementsData,
      });
      setSnackbar?.({
        children: "Elementos adicionados com sucesso!",
        severity: "success",
      });
      onSaveComplete?.();
      setHasChanges(false);
      handleCloseConfirmed();
    } catch (error) {
      console.error("Erro ao salvar elementos:", error);
      setSnackbar?.({
        children: "Erro ao adicionar elementos.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmSaveEdit = async () => {
    setConfirmMode(null);
    setSaving(true);
    const hosts = (tableRef.current?.getRows() as TableRow[]) ?? editData;
    const texts = (textTableRef.current?.getRows() as TextRow[]) ?? editTextData;
    const areas = (areaTableRef.current?.getRows() as AreaRow[]) ?? editAreaData;

    // Hosts (tipo 0/1/2): mesmo contrato do update individual (id, titulo, tipo,
    // font, configuracao, mapRefId, img) — sem mapId no nível superior.
    const hostItems = hosts.map((row) => {
      let hostData = null;
      if (row.type === "Host") {
        const host = hostsCache.find((h: any) => h.host === row.selectedName);
        hostData = host
          ? { hostid: host.hostid, host: host.host, itens: row.hostItems }
          : null;
      }
      return {
        id: row.elementId,
        titulo: row.title || row.selectedName || "",
        tipo: row.type === "Host" ? 0 : row.type === "Mapa" ? 1 : 2,
        font: row.font ?? null,
        img: resolveImgId(row),
        mapRefId: resolveMapRefId(row),
        tags: row.tags ?? [],
        configuracao: {
          coordenadas: { lat: row.lat, long: row.long },
          hosts: row.type === "Host" && hostData ? [hostData] : [],
          produtoId: row.produtoId ?? produtos?.[0]?.id,
          descricao: row.description || null,
        },
      };
    });

    // Textos (tipo 3): preserva o config original e sobrescreve apenas os campos
    // editados (coordenadas + box). O título não é editado, é preservado.
    const textItems = texts.map((row) => ({
      id: row.elementId,
      titulo: row.titulo || "",
      tipo: 3,
      font: row.font ?? null,
      img: row.img ?? null,
      mapRefId: row.mapRefId ?? null,
      tags: row.tags ?? [],
      configuracao: {
        ...(row._origConfig ?? {}),
        coordenadas: { lat: row.lat, long: row.long },
        box: {
          ...(row._origConfig?.box ?? {}),
          mode: row.mode,
          content: row.content,
          textColor: row.textColor,
          backgroundColor: row.backgroundColor,
        },
      },
    }));

    // Áreas (tipo 4): preserva o config original (inclusive polygon.points) e
    // sobrescreve apenas titulo e a cor do polígono.
    const areaItems = areas.map((row) => ({
      id: row.elementId,
      titulo: row.titulo || "",
      tipo: 4,
      font: row.font ?? null,
      img: row.img ?? null,
      mapRefId: row.mapRefId ?? null,
      tags: row.tags ?? [],
      configuracao: {
        ...(row._origConfig ?? {}),
        polygon: {
          ...(row._origConfig?.polygon ?? {}),
          color: row.color,
        },
      },
    }));

    const elementsData = [...hostItems, ...textItems, ...areaItems];

    try {
      await api.put("/easyMaps/updateManyElements", {
        elementsData,
      });
      setSnackbar?.({
        children: "Elementos atualizados com sucesso!",
        severity: "success",
      });
      onSaveComplete?.();
      setHasChanges(false);
      handleCloseConfirmed();
    } catch (error) {
      console.error("Erro ao atualizar elementos:", error);
      setSnackbar?.({
        children: "Erro ao atualizar elementos.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  //  Exclusão em massa (aba "Editar")

  // Lê as linhas atuais da subaba de edição ativa (prefere a instância montada
  // do Handsontable; cai no estado sincronizado como fallback).
  const getActiveEditRows = (): (TableRow | TextRow | AreaRow)[] => {
    if (editSubTab === "textos")
      return (textTableRef.current?.getRows() as TextRow[]) ?? editTextData;
    if (editSubTab === "areas")
      return (areaTableRef.current?.getRows() as AreaRow[]) ?? editAreaData;
    return (tableRef.current?.getRows() as TableRow[]) ?? editData;
  };

  // Exclui os elementos selecionados na subaba ativa via endpoint em massa.
  const handleDeleteSelectedEdit = async () => {
    setConfirmDelete(false);
    const rows = getActiveEditRows();
    const ids = rows
      .filter((row) => row.checked)
      .map((row) => (row as any).elementId)
      .filter(Boolean);
    if (ids.length === 0) return;

    setDeleting(true);
    try {
      let logs: any = null;
      try {
        logs = JSON.parse(localStorage.getItem("logs") || "null");
      } catch {
        logs = null;
      }
      await api.delete("/easyMaps/deleteManyElements", {
        data: { ids, logsLoginClienteId: logs?.id },
      });

      // Remove localmente apenas as linhas selecionadas, preservando edições em
      // andamento nas demais linhas da subaba.
      const remaining = rows.filter((row) => !row.checked);
      if (editSubTab === "textos") setEditTextData([...(remaining as TextRow[])]);
      else if (editSubTab === "areas")
        setEditAreaData([...(remaining as AreaRow[])]);
      else setEditData([...(remaining as TableRow[])]);

      setSnackbar?.({
        children: `${ids.length} elemento(s) excluído(s) com sucesso!`,
        severity: "success",
      });
      // Atualiza o mapa subjacente (remove elementos e conexões excluídos).
      onSaveComplete?.();
    } catch (error) {
      console.error("Erro ao excluir elementos:", error);
      setSnackbar?.({
        children: "Erro ao excluir elementos.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  //  Importação / Exportação / Modelo CSV (apenas aba "Adicionar")

  // Baixa um CSV de exemplo (cabeçalho + linhas de amostra) como modelo.
  const handleDownloadTemplate = () => {
    downloadCsv(exampleCsvTemplate(), "modelo-elementos-ping.csv");
  };

  // Exporta a tabela atual em CSV (mesmo schema da importação → round-trip).
  const handleExportCsv = () => {
    const data = tableRef.current?.getRows() ?? addData;
    const objects = data.map(
      (row) =>
        ({
          tipo: row.type,
          elemento: row.selectedName ?? "",
          titulo: row.title ?? row.selectedName ?? "",
          icone: String(row.iconId ?? ""),
          lat: String(row.lat ?? ""),
          long: String(row.long ?? ""),
          tags: (row.tags ?? []).join(";"),
          descricao: row.description ?? "",
        }) as Record<CsvHeaderKey, string>,
    );
    const bom = String.fromCharCode(0xfeff);
    downloadCsv(bom + rowsToCsv(objects), "elementos-ping.csv");
  };

  // Importa um CSV, preenche a tabela (substituindo o conteúdo) e dá feedback.
  const handleImportCsv = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { records, headerInvalid, empty } = await parseCsvFile(file);

      if (headerInvalid) {
        setSnackbar?.({
          children:
            "Cabeçalho do CSV inválido. Use o modelo (colunas: tipo, elemento, titulo, icone, lat, long, tags, descricao).",
          severity: "error",
        });
        return;
      }
      if (empty) {
        setSnackbar?.({
          children: "Arquivo CSV vazio ou sem linhas de dados.",
          severity: "error",
        });
        return;
      }

      const icons = iconsElement || [];
      const popsByTitulo = new Map<string, any>(
        popsCache.map((p: any) => [p.titulo, p]),
      );
      const mapsByTitulo = new Map<string, any>(
        mapsCache.map((m: any) => [m.titulo, m]),
      );

      let tipoAdjusted = 0;
      let iconAdjusted = 0;
      let rowsWithErrors = 0;

      const rows = records.map((rec) => {
        const { row, tipoDefaulted, iconDefaulted } = mapRecordToRow(rec, {
          icons,
          validIconNames: validNamesLookup.icons,
          popsByTitulo,
          mapsByTitulo,
          normalizeType,
          defaultIconNameForType,
          newId: uuidv4,
        });
        if (tipoDefaulted) tipoAdjusted++;
        if (iconDefaulted) iconAdjusted++;
        row._errors = validateRow(row, validNamesLookup);
        if (Object.keys(row._errors).length > 0) rowsWithErrors++;
        return row;
      });

      // Substitui o conteúdo atual; nova referência → loadData → re-render.
      setAddData(rows.length ? [...rows] : [createEmptyRow()]);
      setHasChanges(true);

      // Mensagem única de feedback (omite cláusulas com contagem zero).
      const parts: string[] = [`${rows.length} linha(s) importada(s).`];
      const adjustments: string[] = [];
      if (tipoAdjusted > 0) adjustments.push(`${tipoAdjusted} tipo(s)`);
      if (iconAdjusted > 0) adjustments.push(`${iconAdjusted} ícone(s)`);
      if (adjustments.length > 0) {
        parts.push(`${adjustments.join(" e ")} ajustado(s) para o padrão.`);
      }
      if (rowsWithErrors > 0) {
        parts.push(`${rowsWithErrors} linha(s) com erros `);
      }
      const severity =
        tipoAdjusted > 0 || iconAdjusted > 0 || rowsWithErrors > 0
          ? "warning"
          : "success";
      setSnackbar?.({ children: parts.join(" "), severity });
    } catch (error) {
      console.error("Erro ao importar CSV:", error);
      setSnackbar?.({
        children: "Não foi possível importar o arquivo CSV.",
        severity: "error",
      });
    } finally {
      // Reseta o valor para permitir reimportar o mesmo arquivo.
      e.target.value = "";
    }
  };

  //  Dados derivados
  const checkedCount = addData.filter((row) => row.checked).length;
  // Contagem de selecionados na subaba de edição ativa (para a exclusão em massa).
  const editActiveData: (TableRow | TextRow | AreaRow)[] =
    editSubTab === "textos"
      ? editTextData
      : editSubTab === "areas"
        ? editAreaData
        : editData;
  const editCheckedCount = editActiveData.filter((row) => row.checked).length;
  // Erros da aba "Adicionar" (gating do botão salvar da adição).
  const addHasErrors = addData.some(
    (row) => Object.keys(validateRow(row, validNamesLookup)).length > 0,
  );
  // Edição: vazio e erros considerando os três datasets (Hosts/Textos/Áreas).
  const editIsEmpty =
    editData.length === 0 &&
    editTextData.length === 0 &&
    editAreaData.length === 0;
  const editHasErrors =
    editData.some(
      (row) => Object.keys(validateRow(row, validNamesLookup)).length > 0,
    ) ||
    editTextData.some((row) => Object.keys(validateTextRow(row)).length > 0) ||
    editAreaData.some((row) => Object.keys(validateAreaRow(row)).length > 0);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "background.paper",
        zIndex: 10,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflow: "hidden",
      }}
    >
      {/* Cabeçalho do modal */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleClose} size="small">
            <ArrowBack />
          </IconButton>
          <Typography id="modal-add-multiple-title" variant="h6" component="h2">
            {activeTab === "add" ? "Adicionar Elementos" : "Editar Elementos"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {activeTab === "add" && (
            <Tooltip title="Importar / Exportar CSV">
              <IconButton
                onClick={(e) => setCsvMenuAnchor(e.currentTarget)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Menu
          anchorEl={csvMenuAnchor}
          open={Boolean(csvMenuAnchor)}
          onClose={() => setCsvMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setCsvMenuAnchor(null);
              importInputRef.current?.click();
            }}
          >
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Importar CSV</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={addData.length === 0}
            onClick={() => {
              setCsvMenuAnchor(null);
              handleExportCsv();
            }}
          >
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exportar CSV</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setCsvMenuAnchor(null);
              handleDownloadTemplate();
            }}
          >
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Baixar modelo CSV</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Abas: Adicionar / Editar */}
      <Tabs
        value={activeTab}
        onChange={(_e, value: TabMode) => setActiveTab(value)}
        sx={{
          minHeight: 0,
          "& .MuiTab-root": {
            minHeight: 32,
            py: 0.5,
            fontSize: 13,
            textTransform: "none",
          },
        }}
      >
        <Tab label="Adicionar" value="add" />
        <Tab label="Editar" value="edit" />
      </Tabs>

      {/* Subabas da edição: cada uma é uma "pill" independente; a selecionada
          fica destacada. */}
      {activeTab === "edit" && (
        <Tabs
          value={editSubTab}
          onChange={(_e, value: EditSubTab) => setEditSubTab(value)}
          sx={{
            minHeight: 0,
            alignSelf: "flex-start",
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 1 },
            "& .MuiTab-root": {
              minHeight: 24,
              minWidth: 0,
              px: 1.5,
              py: 0.25,
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "999px",
              color: "text.secondary",
              bgcolor: "action.hover",
            },
            "& .MuiTab-root.Mui-selected": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
            },
          }}
        >
          <Tab label="Hosts" value="hosts" />
          <Tab label="Textos" value="textos" />
          <Tab label="Áreas" value="areas" />
        </Tabs>
      )}

      {!link && (
        <Typography color="error" variant="body2">
          Nenhum produto vinculado. Não será possível buscar hosts/itens.
        </Typography>
      )}

      {/* Tabela compartilhada (Hosts/adição usam ElementsTable; Textos e Áreas
          usam ShapeElementsTable). Apenas uma tabela fica montada por vez. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {activeTab === "edit" && editSubTab === "textos" ? (
          <ShapeElementsTable
            key="edit-textos"
            ref={textTableRef}
            kind="text"
            data={editTextData}
            onDataChange={(rows) => handleShapeDataChange("textos", rows)}
          />
        ) : activeTab === "edit" && editSubTab === "areas" ? (
          <ShapeElementsTable
            key="edit-areas"
            ref={areaTableRef}
            kind="area"
            data={editAreaData}
            onDataChange={(rows) => handleShapeDataChange("areas", rows)}
          />
        ) : (
          <ElementsTable
            key={activeTab === "add" ? "add" : "edit-hosts"}
            ref={tableRef}
            mode={activeTab}
            data={activeTab === "add" ? addData : editData}
            onDataChange={handleTableDataChange}
            hostsCache={hostsCache}
            popsCache={popsCache}
            mapsCache={mapsCache}
            iconsElement={iconsElement}
            validNamesLookup={validNamesLookup}
            onAddCustomIcon={() => setOpenModalImg(true)}
            tagsCache={tagsCache}
            onCreateTag={handleCreateTag}
          />
        )}
      </Box>

      {/* Botões de ação */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleImportCsv}
          />
          {activeTab === "add" && checkedCount > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
              size="small"
            >
              Remover ({checkedCount})
            </Button>
          )}
          {activeTab === "edit" && editCheckedCount > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
              size="small"
            >
              Remover ({editCheckedCount})
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          {activeTab === "add" ? (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                disabled={!link}
              >
                Adicionar Linha
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => validateActiveAndConfirm("add")}
                disabled={saving || addData.length === 0 || addHasErrors}
              >
                Salvar
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => validateActiveAndConfirm("edit")}
              disabled={saving || editIsEmpty || editHasErrors}
            >
              Salvar alterações
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Modal de Adicionar Ícones Customizados */}
      <ImageUploadModal
        api={api}
        icon={null}
        onUpload={() => {}}
        setSnackbar={setSnackbar}
        open={openModalImg}
        onClose={() => setOpenModalImg(false)}
        setIconElement={() => {}}
        setIconsElement={setIconsElement}
        iconsElement={iconsElement}
      />

      {/* Modal de confirmação antes de salvar */}
      <AlertModal
        openModal={confirmMode !== null}
        setOpenModal={(value: boolean) => {
          if (!value) setConfirmMode(null);
        }}
        singleButton={false}
        severity="info"
        textContent={
          confirmMode === "edit"
            ? "Deseja confirmar a edição dos elementos?"
            : "Deseja confirmar a adição dos elementos?"
        }
        buttonText="Confirmar"
        buttonCancelText="Cancelar"
        handleButton={confirmMode === "edit" ? confirmSaveEdit : confirmSave}
      />

      {/* Modal de confirmação antes de excluir (exclusão em massa na edição) */}
      <AlertModal
        openModal={confirmDelete}
        setOpenModal={setConfirmDelete}
        singleButton={false}
        severity="warning"
        textContent={`Deseja excluir os ${editCheckedCount} elemento(s) selecionado(s)? Esta ação não pode ser desfeita.`}
        buttonText="Excluir"
        buttonCancelText="Cancelar"
        handleButton={handleDeleteSelectedEdit}
      />

      {/* Modal de alerta para alterações não salvas */}
      <AlertModal
        openModal={unsavedAlertOpen}
        setOpenModal={setUnsavedAlertOpen}
        singleButton={false}
        severity="warning"
        textContent="Existem alterações não salvas. Deseja realmente sair?"
        buttonText="Sair"
        buttonCancelText="Cancelar"
        handleButton={handleUnsavedConfirm}
        handleCancel={handleUnsavedCancel}
      />
    </Box>
  );
};

export default ModalAddMultiple;
